import React, { useMemo, useState } from "react";
import { useGlobalData } from "../hooks/useGlobalHooks";
import { Check, X, ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import { Button } from "../components/ui/button";
import apiClient from "../utils/apiClient";
import toast from "react-hot-toast";
import server from "../utils/serverConfig";

function CompactDashboard() {
  const { positions, orders, userMappings, wsConnected, loading } =
    useGlobalData();

  // Compute derived values that were in old global-positions provider
  const liveOrders = orders || [];
  const totalPnL = useMemo(() => {
    return positions?.reduce((sum, pos) => sum + (pos.pl || 0), 0) || 0;
  }, [positions]);
  const connectionStatus = wsConnected ? "Connected" : "Disconnected";
  const userData = useMemo(() => {
    // Convert userMappings array to object keyed by client_id
    const userObj = {};
    userMappings?.forEach((user) => {
      userObj[user.client_id] = user;
    });
    return userObj;
  }, [userMappings]);
  const userDataLoading = loading?.userMappings || false;

  // Sorting states for each table
  const [topPerformerSort, setTopPerformerSort] = useState({
    column: null,
    direction: null,
  });
  const [worstPerformerSort, setWorstPerformerSort] = useState({
    column: null,
    direction: null,
  });
  const [positionVsSellSort, setPositionVsSellSort] = useState({
    column: null,
    direction: null,
  });
  const [clientSummarySort, setClientSummarySort] = useState({
    column: null,
    direction: null,
  });
  const [negativePositionsSort, setNegativePositionsSort] = useState({
    column: null,
    direction: null,
  });

  const strategyOptions = [
    "HE_PC5_MA10",
    "HE_PC2_MA10",
    "HHPC_PC2_MA10",
    "HHPC_PC5_MA10",
    "PDI_MDI_PC2_MA10",
    "PDI_MDI_PC5_MA10",
    "HE_PC5",
    "HE_PC2",
    "PDI_MA_PC2",
    "PDI_MA_PC5",
    "PDI_MDI_PC2",
    "PDI_MDI_PC5",
    "PDI_MA_PC2_MA10",
    "PDI_MA_PC5_MA10",
    "HHPC_PC2",
    "HHPC_PC5",
    "ORB_1",
  ];

  // Generic sort handler
  const handleSort = (column, currentSort, setSort) => {
    if (currentSort.column === column) {
      if (currentSort.direction === "asc") {
        setSort({ column, direction: "desc" });
      } else if (currentSort.direction === "desc") {
        setSort({ column: null, direction: null }); // Reset
      }
    } else {
      setSort({ column, direction: "asc" });
    }
  };

  // Sort array helper
  const sortArray = (array, sortConfig, defaultSort = null) => {
    if (!sortConfig.column) {
      if (defaultSort) {
        return [...array].sort(defaultSort);
      }
      return array;
    }

    return [...array].sort((a, b) => {
      let aVal = a[sortConfig.column];
      let bVal = b[sortConfig.column];

      // Handle special cases
      if (sortConfig.column === "side") {
        aVal = parseInt(a.side) === 1 && a.net_qty > 0 ? "BUY" : "SELL";
        bVal = parseInt(b.side) === 1 && b.net_qty > 0 ? "BUY" : "SELL";
      }

      // Handle null/undefined - treat empty strategy as 'Manual' or 'N/A'
      if (sortConfig.column === "strategy") {
        aVal = aVal || "Manual";
        bVal = bVal || "Manual";
      }

      // Convert to numbers if the values are numeric strings
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);
      if (
        !isNaN(aNum) &&
        !isNaN(bNum) &&
        typeof aVal === "string" &&
        typeof bVal === "string"
      ) {
        aVal = aNum;
        bVal = bNum;
      }

      // Handle remaining null/undefined
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // String comparison (case-insensitive for text)
      if (typeof aVal === "string" && typeof bVal === "string") {
        const comparison = aVal.toLowerCase().localeCompare(bVal.toLowerCase());
        return sortConfig.direction === "asc" ? comparison : -comparison;
      }

      // Numeric comparison
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  // Render sort indicator
  const SortIcon = ({ column, currentSort, onSort }) => {
    const isActive = currentSort.column === column;

    return (
      <span
        onClick={(e) => {
          e.stopPropagation();
          onSort();
        }}
        className="inline-flex items-center ml-1 cursor-pointer hover:text-blue-400 transition-colors"
      >
        {!isActive && <ChevronsUpDown className="w-3 h-3 text-zinc-500" />}
        {isActive && currentSort.direction === "asc" && (
          <ArrowUp className="w-3 h-3 text-blue-400" />
        )}
        {isActive && currentSort.direction === "desc" && (
          <ArrowDown className="w-3 h-3 text-blue-400" />
        )}
      </span>
    );
  };

  // Helper function to get user info by client_id
  const getUserInfo = (clientId) => {
    if (!clientId)
      return { name: "N/A", multiplier: 0, balance: 0, broker: "N/A" };
    const user = userData[clientId];
    if (!user) return { name: "N/A", multiplier: 0, balance: 0, broker: "N/A" };
    return {
      name: user.name || "N/A",
      multiplier: user.index_multiplier || 0,
      banknifty_multiplier: user.banknifty_multiplier || 0,
      finnifty_multiplier: user.finnifty_multiplier || 0,
      midcpnifty_multiplier: user.midcpnifty_multiplier || 0,
      sensex_multiplier: user.sensex_multiplier || 0,
      mcx_multiplier: user.mcx_multiplier || 0,
      stock_multiplier: user.stock_multiplier || 0,
      balance: user.balance || 0,
      broker: user.broker || "N/A",
    };
  };

  // Helper to render broker badges (handles values like 'dhan_anurag' or 'fyers')
  const getBrokerClass = (brokerLower) => {
    if (!brokerLower) return "bg-gray-700 text-white";
    if (brokerLower.includes("fyers")) return "bg-blue-700 text-white";
    if (brokerLower.includes("dhan")) return "bg-emerald-700 text-white";
    if (brokerLower.includes("nubra")) return "bg-purple-700 text-white";
    return "bg-gray-700 text-white";
  };

  const renderBrokerBadges = (broker) => {
    if (!broker) return <span className="text-zinc-400">-</span>;

    // split on common delimiters (underscore, space, comma) and remove empties
    const parts = String(broker)
      .split(/[_\s,|]+/)
      .filter(Boolean);

    return (
      <div className="flex items-center justify-center gap-1 flex-wrap">
        {parts.map((p, i) => (
          <span
            key={i}
            className={`px-2 py-0.5 rounded text-[10px] font-semibold ${getBrokerClass(
              String(p).toLowerCase()
            )} truncate`}
            title={p}
          >
            {p}
          </span>
        ))}
      </div>
    );
  };

  const handleExitPosition = (currentPositionData) => {
    if (currentPositionData !== null) {
      console.log("EXIT Position", currentPositionData);

      let config = {
        method: "post",
        url: `${server.godseye.baseUrl}exit_positions`,
        headers: {
          "Content-Type": "application/json",
        },
        data: JSON.stringify(currentPositionData),
      };

      apiClient
        .request(config)
        .then((response) => {
          console.log("Exit Position response:", response.data);

          if (response.data.status === 200) {
            toast.success("Position Exit Successful !!", {
              duration: 5000,
            });
          } else {
            toast.error("Position Exit Failed !!", {
              duration: 5000,
            });
          }
        })
        .catch((error) => {
          console.log("Error in exit position request:", error);
        });
    } else {
      alert("Please select a position for exit");
    }
  };

  // Calculate metrics
  const metrics = useMemo(() => {
    console.log("=== STRATEGY COUNT DEBUG ===");
    console.log("Total positions received:", positions.length);

    const strategyCounts = {};
    positions.forEach((p) => {
      const strategy = p.strategy || "Manual";
      strategyCounts[strategy] = (strategyCounts[strategy] || 0) + 1;
    });

    console.log("Strategy counts:", strategyCounts);
    console.log(
      "PDI_MA_PC2 positions:",
      positions.filter((p) => p.strategy === "PDI_MA_PC2").length
    );
    console.log(
      "PDI_MA_PC5 positions:",
      positions.filter((p) => p.strategy === "PDI_MA_PC5").length
    );

    const zeroLtpPositions = positions.filter((p) => parseFloat(p.ltp) === 0);
    const zeroPLPositions = positions.filter((p) => parseFloat(p.pl) === 0);
    console.log("Positions with LTP=0:", zeroLtpPositions.length);
    console.log("Positions with PL=0:", zeroPLPositions.length);
    console.log("=== END DEBUG ===");

    const totalPositions = positions.length;
    const profitablePositions = positions.filter(
      (p) => (parseFloat(p.pl) || 0) > 0
    ).length;
    const lossPositions = positions.filter(
      (p) => (parseFloat(p.pl) || 0) < 0
    ).length;

    const totalInvestment = positions.reduce((sum, p) => {
      const netAvg = parseFloat(p.net_avg) || 0;
      const netQty = parseFloat(p.net_qty) || 0;
      return sum + netAvg * Math.abs(netQty);
    }, 0);

    const currentValue = positions.reduce((sum, p) => {
      const currentPrice = parseFloat(p.ltp) || parseFloat(p.net_avg) || 0;
      const netQty = parseFloat(p.net_qty) || 0;
      return sum + currentPrice * Math.abs(netQty);
    }, 0);

    const topPerformers = positions
      .filter((p) => parseFloat(p.net_qty || 0) > 0) // Only positive quantities
      .sort((a, b) => (parseFloat(b.pl) || 0) - (parseFloat(a.pl) || 0))
      .slice(0, 10);

    const worstPerformers = positions
      .filter(
        (p) => (parseFloat(p.pl) || 0) < 0 && parseFloat(p.net_qty || 0) > 0
      ) // Only loss positions with positive qty
      .sort((a, b) => (parseFloat(a.pl) || 0) - (parseFloat(b.pl) || 0))
      .slice(0, 10);

    const strategies = positions.reduce((acc, p) => {
      const strategy = p.strategy || "N/A";
      if (!acc[strategy]) {
        acc[strategy] = { count: 0, pnl: 0 };
      }
      acc[strategy].count++;
      acc[strategy].pnl += parseFloat(p.pl) || 0;
      return acc;
    }, {});

    const positionData = positions.reduce((acc, p) => {
      const key = `${p.client_id}_${p.symbol}`;
      const qty = Math.abs(parseFloat(p.net_qty) || 0);

      if (!acc[key]) {
        acc[key] = {
          client_id: p.client_id,
          symbol: p.symbol,
          positionQty: 0,
          sellOrderQty: 0,
        };
      }

      acc[key].positionQty += qty;
      return acc;
    }, {});

    if (liveOrders && Array.isArray(liveOrders)) {
      console.log("=== ORDER MATCHING DEBUG ===");
      console.log(
        "Sample position keys:",
        Object.keys(positionData).slice(0, 3)
      );

      const sellOrdersGrouped = liveOrders
        .filter((order) => order.side === -1)
        .reduce((acc, order) => {
          const key = `${order.clientId}_${order.symbol}`;
          const orderQty = Math.abs(parseFloat(order.qty) || 0);

          console.log(`Processing order: ${key} with qty ${orderQty}`);

          if (!acc[key]) {
            acc[key] = 0;
          }
          acc[key] += orderQty;
          return acc;
        }, {});

      console.log("Grouped sell orders:", sellOrdersGrouped);
      console.log("Position data keys:", Object.keys(positionData).slice(0, 5));

      Object.entries(sellOrdersGrouped).forEach(([key, totalQty]) => {
        if (positionData[key]) {
          positionData[key].sellOrderQty = totalQty;
          console.log(
            `✅ Matched: ${key} - Position: ${positionData[key].positionQty}, Orders: ${totalQty}`
          );
        } else {
          console.log(`❌ No position found for order key: ${key}`);
        }
      });

      console.log("=== END ORDER MATCHING DEBUG ===");
    }

    const buySellData = Object.values(positionData)
      .filter((item) => item.positionQty > 0)
      .map((item, index) => ({
        ...item,
        index: index + 1,
        clientName: getUserInfo(item.client_id).name,
        isMatched: Math.abs(item.positionQty - item.sellOrderQty) === 0,
      }));

    const negativePositions = [];
    positions.forEach((position, index) => {
      const hasNegativeQty =
        parseFloat(position.net_qty) < 0 || parseInt(position.side) === -1;

      if (hasNegativeQty) {
        negativePositions.push({
          id:
            position.key || `${position.client_id}_${position.symbol}_${index}`,
          index: negativePositions.length + 1,
          ltp: position.ltp,
          pl: position.pl,
          side: position.side,
          symbol: position.symbol,
          clientIds: position.client_id ? [position.client_id] : [],
          netQty: position.net_qty,
          netAvg: position.net_avg,
          strategy: position.strategy,
          key:
            position.key || `${position.client_id}_${position.symbol}_${index}`,
          client_id: position.client_id,
          clientName: getUserInfo(position.client_id).name,
          qty: position.net_qty,
        });
        console.log(
          `❌ Position has negative Qty: ${position.key || position.symbol}`
        );
        console.log(
          `   └─ Symbol: ${position.symbol} | Client: ${position.client_id} | Strategy: ${position.strategy} | NetQty: ${position.net_qty}`
        );
      }
    });

    return {
      totalPositions,
      profitablePositions,
      lossPositions,
      totalInvestment,
      currentValue,
      topPerformers,
      worstPerformers,
      strategies: Object.entries(strategies),
      buySellData,
      negativePositions,
    };
  }, [positions, liveOrders, userData]);

  // Apply sorting to metrics
  const sortedTopPerformers = useMemo(() => {
    return sortArray(
      metrics.topPerformers,
      topPerformerSort,
      (a, b) => (parseFloat(b.pl) || 0) - (parseFloat(a.pl) || 0)
    );
  }, [metrics.topPerformers, topPerformerSort]);

  const sortedWorstPerformers = useMemo(() => {
    return sortArray(
      metrics.worstPerformers,
      worstPerformerSort,
      (a, b) => (parseFloat(a.pl) || 0) - (parseFloat(b.pl) || 0)
    );
  }, [metrics.worstPerformers, worstPerformerSort]);

  const sortedPositionVsSell = useMemo(() => {
    return sortArray(metrics.buySellData, positionVsSellSort);
  }, [metrics.buySellData, positionVsSellSort]);

  const sortedClientSummary = useMemo(() => {
    const clientData = Object.entries(
      positions.reduce((acc, p) => {
        const client = p.client_id || "N/A";
        if (!acc[client]) {
          acc[client] = {
            client_id: client,
            count: 0,
            pnl: 0,
            ...getUserInfo(client),
          };
        }
        acc[client].count++;
        acc[client].pnl += parseFloat(p.pl) || 0;
        return acc;
      }, {})
    ).map(([client, data]) => data);

    return sortArray(clientData, clientSummarySort);
  }, [positions, userData, clientSummarySort]);

  const sortedNegativePositions = useMemo(() => {
    return sortArray(metrics.negativePositions, negativePositionsSort);
  }, [metrics.negativePositions, negativePositionsSort]);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "₹0.00";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatCompactCurrency = (amount) => {
    if (amount === null || amount === undefined) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="p-4 bg-zinc-900 min-h-screen">
      {/* Compact Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-white">Trading Dashboard</h1>
        <div className="flex items-center space-x-3">
          <div
            className={`w-2 h-2 rounded-full ${
              connectionStatus === "Connected"
                ? "bg-green-400 animate-pulse"
                : "bg-red-400"
            }`}
          ></div>
          <span className="text-sm text-zinc-300">{connectionStatus}</span>
          {userDataLoading && (
            <span className="text-xs text-yellow-400">Loading users...</span>
          )}
        </div>
      </div>

      {/* Compact Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
        <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
          <div className="text-xs text-zinc-400 mb-1">Total Positions</div>
          <div className="text-lg font-bold text-white">
            {metrics.totalPositions}
          </div>
        </div>

        <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
          <div className="text-xs text-zinc-400 mb-1">Profitable</div>
          <div className="text-lg font-bold text-green-400">
            {metrics.profitablePositions}
          </div>
        </div>

        <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
          <div className="text-xs text-zinc-400 mb-1">Loss Making</div>
          <div className="text-lg font-bold text-red-400">
            {metrics.lossPositions}
          </div>
        </div>

        <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
          <div className="text-xs text-zinc-400 mb-1">Total P&L</div>
          <div
            className={`text-lg font-bold ${
              totalPnL >= 0 ? "text-green-400" : "text-red-400"
            }`}
          >
            {formatCurrency(totalPnL)}
          </div>
        </div>

        <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
          <div className="text-xs text-zinc-400 mb-1">Investment</div>
          <div className="text-lg font-bold text-white">
            {formatCompactCurrency(metrics.totalInvestment)}
          </div>
        </div>

        <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
          <div className="text-xs text-zinc-400 mb-1">Current Value</div>
          <div className="text-lg font-bold text-white">
            {formatCompactCurrency(metrics.currentValue)}
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Performers Table */}
        <div className="bg-zinc-800 rounded border border-zinc-700">
          <div className="px-4 py-3 border-b border-zinc-700">
            <h2 className="text-sm font-semibold text-white">Top Performers</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-max">
              <thead className="bg-zinc-700/50">
                <tr className="h-8">
                  <th className="px-3 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[32px]">
                    #
                  </th>
                  <th className="px-3 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[120px]">
                    Symbol
                    <SortIcon
                      column="symbol"
                      currentSort={topPerformerSort}
                      onSort={() =>
                        handleSort(
                          "symbol",
                          topPerformerSort,
                          setTopPerformerSort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-2 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[60px]">
                    Side
                    <SortIcon
                      column="side"
                      currentSort={topPerformerSort}
                      onSort={() =>
                        handleSort(
                          "side",
                          topPerformerSort,
                          setTopPerformerSort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">
                    Qty
                    <SortIcon
                      column="net_qty"
                      currentSort={topPerformerSort}
                      onSort={() =>
                        handleSort(
                          "net_qty",
                          topPerformerSort,
                          setTopPerformerSort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">
                    Avg Price
                    <SortIcon
                      column="net_avg"
                      currentSort={topPerformerSort}
                      onSort={() =>
                        handleSort(
                          "net_avg",
                          topPerformerSort,
                          setTopPerformerSort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">
                    LTP
                    <SortIcon
                      column="ltp"
                      currentSort={topPerformerSort}
                      onSort={() =>
                        handleSort("ltp", topPerformerSort, setTopPerformerSort)
                      }
                    />
                  </th>
                  <th className="px-3 py-2 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">
                    Strategy
                    <SortIcon
                      column="strategy"
                      currentSort={topPerformerSort}
                      onSort={() =>
                        handleSort(
                          "strategy",
                          topPerformerSort,
                          setTopPerformerSort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-2 text-right text-zinc-300 font-medium min-w-[100px]">
                    P&L
                    <SortIcon
                      column="pl"
                      currentSort={topPerformerSort}
                      onSort={() =>
                        handleSort("pl", topPerformerSort, setTopPerformerSort)
                      }
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedTopPerformers.map((position, index) => {
                  const currentPrice =
                    parseFloat(position.ltp) ||
                    parseFloat(position.net_avg) ||
                    0;
                  const netAvg = parseFloat(position.net_avg) || 0;

                  return (
                    <tr
                      key={position.id || index}
                      className="h-8 hover:bg-zinc-700/30 border-b border-zinc-700/30"
                    >
                      <td className="px-3 py-1 text-zinc-400 border-r border-zinc-700/30">
                        {index + 1}
                      </td>
                      <td className="px-3 py-1 border-r border-zinc-700/30">
                        <div className="text-white font-medium truncate">
                          {position.symbol || ""}
                        </div>
                      </td>
                      <td
                        className={`px-3 py-1 text-center border-r border-zinc-700/30 font-medium ${
                          parseInt(position.side) === 1 && position.net_qty > 0
                            ? "text-green-400"
                            : "text-red-400"
                        }`}
                      >
                        {parseInt(position.side) === 1 && position.net_qty > 0
                          ? "BUY"
                          : "SELL"}
                      </td>
                      <td className="px-3 py-1 text-right text-white border-r border-zinc-700/30">
                        {position.net_qty?.toLocaleString()}
                      </td>
                      <td className="px-3 py-1 text-right text-white border-r border-zinc-700/30">
                        ₹{netAvg.toFixed(2)}
                      </td>
                      <td className="px-3 py-1 text-right text-white border-r border-zinc-700/30">
                        ₹{currentPrice.toFixed(2)}
                      </td>
                      <td className="px-3 py-1 text-center text-zinc-300 border-r border-zinc-700/30 truncate">
                        {position.strategy || "N/A"}
                      </td>
                      <td
                        className={`px-3 py-1 text-right font-medium font-mono ${
                          (parseFloat(position.pl) || 0) >= 0
                            ? "text-green-400"
                            : "text-red-400"
                        }`}
                      >
                        {formatCurrency(parseFloat(position.pl) || 0)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Worst Performers */}
        <div className="bg-zinc-800 rounded border border-zinc-700">
          <div className="px-4 py-3 border-b border-zinc-700">
            <h2 className="text-sm font-semibold text-white">
              Worst Performers
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-max">
              <thead className="bg-zinc-700 sticky top-0">
                <tr className="h-8">
                  <th className="px-3 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[32px]">
                    #
                  </th>
                  <th className="px-3 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[120px]">
                    Symbol
                    <SortIcon
                      column="symbol"
                      currentSort={worstPerformerSort}
                      onSort={() =>
                        handleSort(
                          "symbol",
                          worstPerformerSort,
                          setWorstPerformerSort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-2 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[60px]">
                    Side
                    <SortIcon
                      column="side"
                      currentSort={worstPerformerSort}
                      onSort={() =>
                        handleSort(
                          "side",
                          worstPerformerSort,
                          setWorstPerformerSort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">
                    Qty
                    <SortIcon
                      column="net_qty"
                      currentSort={worstPerformerSort}
                      onSort={() =>
                        handleSort(
                          "net_qty",
                          worstPerformerSort,
                          setWorstPerformerSort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">
                    Avg Price
                    <SortIcon
                      column="net_avg"
                      currentSort={worstPerformerSort}
                      onSort={() =>
                        handleSort(
                          "net_avg",
                          worstPerformerSort,
                          setWorstPerformerSort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">
                    LTP
                    <SortIcon
                      column="ltp"
                      currentSort={worstPerformerSort}
                      onSort={() =>
                        handleSort(
                          "ltp",
                          worstPerformerSort,
                          setWorstPerformerSort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-2 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">
                    Strategy
                    <SortIcon
                      column="strategy"
                      currentSort={worstPerformerSort}
                      onSort={() =>
                        handleSort(
                          "strategy",
                          worstPerformerSort,
                          setWorstPerformerSort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-2 text-right text-zinc-300 font-medium min-w-[100px]">
                    P&L
                    <SortIcon
                      column="pl"
                      currentSort={worstPerformerSort}
                      onSort={() =>
                        handleSort(
                          "pl",
                          worstPerformerSort,
                          setWorstPerformerSort
                        )
                      }
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedWorstPerformers.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-3 py-8 text-center text-zinc-400 text-xl"
                    >
                      No Loss Positions
                    </td>
                  </tr>
                ) : (
                  sortedWorstPerformers.map((position, index) => {
                    const currentPrice =
                      parseFloat(position.ltp) ||
                      parseFloat(position.net_avg) ||
                      0;
                    const netAvg = parseFloat(position.net_avg) || 0;

                    return (
                      <tr
                        key={position.id || index}
                        className="h-8 hover:bg-zinc-700/30 border-b border-zinc-700/30"
                      >
                        <td className="px-3 py-1 text-zinc-400 border-r border-zinc-700/30">
                          {index + 1}
                        </td>
                        <td className="px-3 py-1 border-r border-zinc-700/30">
                          <div className="text-white font-medium truncate">
                            {position.symbol || ""}
                          </div>
                        </td>
                        <td
                          className={`px-3 py-1 text-center border-r border-zinc-700/30 font-medium ${
                            parseInt(position.side) === 1 &&
                            position.net_qty > 0
                              ? "text-green-400"
                              : "text-red-400"
                          }`}
                        >
                          {parseInt(position.side) === 1 && position.net_qty > 0
                            ? "BUY"
                            : "SELL"}
                        </td>
                        <td className="px-3 py-1 text-right text-white border-r border-zinc-700/30">
                          {position.net_qty?.toLocaleString()}
                        </td>
                        <td className="px-3 py-1 text-right text-white border-r border-zinc-700/30">
                          ₹{netAvg.toFixed(2)}
                        </td>
                        <td className="px-3 py-1 text-right text-white border-r border-zinc-700/30">
                          ₹{currentPrice.toFixed(2)}
                        </td>
                        <td className="px-3 py-1 text-center text-zinc-300 border-r border-zinc-700/30 truncate">
                          {position.strategy || "N/A"}
                        </td>
                        <td
                          className={`px-3 py-1 text-right font-medium font-mono ${
                            (parseFloat(position.pl) || 0) >= 0
                              ? "text-green-400"
                              : "text-red-400"
                          }`}
                        >
                          {formatCurrency(parseFloat(position.pl) || 0)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Three Column Layout for Additional Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {/* Position vs Sell Order Qty Comparison */}
        {/* <div className="bg-zinc-800 rounded border border-zinc-700">
          <div className="px-4 py-3 border-b border-zinc-700">
            <h2 className="text-sm font-semibold text-white">
              Position vs Sell Order Qty
            </h2>
            <div className="text-xs text-zinc-400 mt-1">
              Covered:{" "}
              {metrics.buySellData.filter((item) => item.isMatched).length} /{" "}
              {metrics.buySellData.length}
              {!liveOrders && (
                <span className="text-yellow-400 ml-2">
                  (Orders data not available)
                </span>
              )}
            </div>
          </div>

          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-xs">
              <thead className="bg-zinc-700 sticky top-0">
                <tr className="h-8">
                  <th className="px-2 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30 w-8">
                    #
                    <SortIcon
                      column="index"
                      currentSort={positionVsSellSort}
                      onSort={() =>
                        handleSort(
                          "index",
                          positionVsSellSort,
                          setPositionVsSellSort
                        )
                      }
                    />
                  </th>
                  <th className="px-2 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30">
                    Client ID
                    <SortIcon
                      column="client_id"
                      currentSort={positionVsSellSort}
                      onSort={() =>
                        handleSort(
                          "client_id",
                          positionVsSellSort,
                          setPositionVsSellSort
                        )
                      }
                    />
                  </th>
                  <th className="px-2 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30">
                    Client Name
                    <SortIcon
                      column="clientName"
                      currentSort={positionVsSellSort}
                      onSort={() =>
                        handleSort(
                          "clientName",
                          positionVsSellSort,
                          setPositionVsSellSort
                        )
                      }
                    />
                  </th>
                  <th className="px-2 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30">
                    Symbol
                    <SortIcon
                      column="symbol"
                      currentSort={positionVsSellSort}
                      onSort={() =>
                        handleSort(
                          "symbol",
                          positionVsSellSort,
                          setPositionVsSellSort
                        )
                      }
                    />
                  </th>
                  <th className="px-2 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30">
                    Position Qty
                    <SortIcon
                      column="positionQty"
                      currentSort={positionVsSellSort}
                      onSort={() =>
                        handleSort(
                          "positionQty",
                          positionVsSellSort,
                          setPositionVsSellSort
                        )
                      }
                    />
                  </th>
                  <th className="px-2 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30">
                    Sell Qty
                    <SortIcon
                      column="sellOrderQty"
                      currentSort={positionVsSellSort}
                      onSort={() =>
                        handleSort(
                          "sellOrderQty",
                          positionVsSellSort,
                          setPositionVsSellSort
                        )
                      }
                    />
                  </th>
                  <th className="px-2 py-2 text-center text-zinc-300 font-medium">
                    Equal?
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedPositionVsSell.map((item, index) => {
                  return (
                    <tr
                      key={index}
                      className={`h-8 hover:bg-zinc-700/30 border-b border-zinc-700/30 ${
                        !item.isMatched ? "bg-red-900/20" : ""
                      }`}
                    >
                      <td className="px-2 py-1 text-zinc-400 border-r border-zinc-700/30">
                        {index + 1}
                      </td>
                      <td className="px-2 py-1 text-white border-r border-zinc-700/30 truncate text-xs">
                        {item.client_id || "N/A"}
                      </td>
                      <td className="px-2 py-1 text-white border-r border-zinc-700/30 truncate text-xs">
                        {item.clientName}
                      </td>
                      <td className="px-2 py-1 text-white border-r border-zinc-700/30 truncate">
                        {(item.symbol || "").replace("NSE:", "")}
                      </td>
                      <td className="px-2 py-1 text-right text-blue-400 border-r border-zinc-700/30 font-mono">
                        {item.positionQty.toFixed(0)}
                      </td>
                      <td className="px-2 py-1 text-right text-orange-400 border-r border-zinc-700/30 font-mono">
                        {item.sellOrderQty.toFixed(0)}
                      </td>
                      <td className="px-2 py-1 text-center">
                        {item.isMatched ? (
                          <Check className="w-4 h-4 text-green-400 mx-auto" />
                        ) : (
                          <X className="w-4 h-4 text-red-400 mx-auto" />
                        )}
                      </td>
                    </tr>
                  );
                })}
                {sortedPositionVsSell.length === 0 && (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-4 py-8 text-center text-zinc-400"
                    >
                      {!liveOrders
                        ? "Orders data not available"
                        : "No positions found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div> */}

        {/* Strategy Breakdown - NO SORTING */}
        <div className="bg-zinc-800 rounded border border-zinc-700">
          <div className="px-4 py-3 border-b border-zinc-700">
            <h2 className="text-sm font-semibold text-white">
              Strategy Breakdown
            </h2>
            <div className="text-xs text-zinc-400 mt-1">
              Total: {metrics.totalPositions} positions
            </div>
          </div>

          <div className="p-4 space-y-3">
            {metrics.strategies.map(([strategy, data], index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  <div className="text-sm text-white font-medium truncate">
                    {strategy}
                  </div>
                  <div className="text-xs text-zinc-400 whitespace-nowrap">
                    {data.count} positions
                  </div>
                </div>
                <div
                  className={`text-sm font-semibold font-mono ml-2 ${
                    data.pnl >= 0 ? "text-green-400" : "text-red-400"
                  }`}
                >
                  {formatCurrency(data.pnl)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Client Summary */}
        {/* <div className="bg-zinc-800 rounded border border-zinc-700">
          <div className="px-4 py-3 border-b border-zinc-700">
            <h2 className="text-sm font-semibold text-white">Client Summary</h2>
          </div>

          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-xs min-w-max">
              <thead className="bg-zinc-700/50">
                <tr className="h-7">
                  <th className="px-3 py-1 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">
                    Client ID
                    <SortIcon
                      column="client_id"
                      currentSort={clientSummarySort}
                      onSort={() =>
                        handleSort(
                          "client_id",
                          clientSummarySort,
                          setClientSummarySort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-1 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[120px]">
                    Client Name
                    <SortIcon
                      column="name"
                      currentSort={clientSummarySort}
                      onSort={() =>
                        handleSort(
                          "name",
                          clientSummarySort,
                          setClientSummarySort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-1 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[60px]">
                    Positions
                    <SortIcon
                      column="count"
                      currentSort={clientSummarySort}
                      onSort={() =>
                        handleSort(
                          "count",
                          clientSummarySort,
                          setClientSummarySort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-1 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[70px]">
                    Multiplier
                    <SortIcon
                      column="multiplier"
                      currentSort={clientSummarySort}
                      onSort={() =>
                        handleSort(
                          "multiplier",
                          clientSummarySort,
                          setClientSummarySort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-1 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[70px]">
                    BNF Multiplier
                    <SortIcon
                      column="banknifty_multiplier"
                      currentSort={clientSummarySort}
                      onSort={() =>
                        handleSort(
                          "banknifty_multiplier",
                          clientSummarySort,
                          setClientSummarySort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-1 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[70px]">
                    FINNIFTY Multiplier
                    <SortIcon
                      column="finnifty_multiplier"
                      currentSort={clientSummarySort}
                      onSort={() =>
                        handleSort(
                          "finnifty_multiplier",
                          clientSummarySort,
                          setClientSummarySort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-1 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[70px]">
                    MIDCP Multiplier
                    <SortIcon
                      column="midcpnifty_multiplier"
                      currentSort={clientSummarySort}
                      onSort={() =>
                        handleSort(
                          "midcpnifty_multiplier",
                          clientSummarySort,
                          setClientSummarySort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-1 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[70px]">
                    SENSEX Multiplier
                    <SortIcon
                      column="sensex_multiplier"
                      currentSort={clientSummarySort}
                      onSort={() =>
                        handleSort(
                          "sensex_multiplier",
                          clientSummarySort,
                          setClientSummarySort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-1 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[70px]">
                    MCX Multiplier
                    <SortIcon
                      column="mcx_multiplier"
                      currentSort={clientSummarySort}
                      onSort={() =>
                        handleSort(
                          "mcx_multiplier",
                          clientSummarySort,
                          setClientSummarySort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-1 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">
                    Balance
                    <SortIcon
                      column="balance"
                      currentSort={clientSummarySort}
                      onSort={() =>
                        handleSort(
                          "balance",
                          clientSummarySort,
                          setClientSummarySort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-1 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[60px]">
                    Broker
                    <SortIcon
                      column="broker"
                      currentSort={clientSummarySort}
                      onSort={() =>
                        handleSort(
                          "broker",
                          clientSummarySort,
                          setClientSummarySort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-1 text-right text-zinc-300 font-medium min-w-[100px]">
                    P&L
                    <SortIcon
                      column="pnl"
                      currentSort={clientSummarySort}
                      onSort={() =>
                        handleSort(
                          "pnl",
                          clientSummarySort,
                          setClientSummarySort
                        )
                      }
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedClientSummary.map((client, index) => (
                  <tr
                    key={index}
                    className="h-7 hover:bg-zinc-700/30 border-b border-zinc-700/30"
                  >
                    <td className="px-3 py-1 text-white border-r border-zinc-700/30 truncate">
                      {client.client_id}
                    </td>
                    <td className="px-3 py-1 text-white border-r border-zinc-700/30 truncate">
                      {client.name}
                    </td>
                    <td className="px-3 py-1 text-center text-zinc-300 border-r border-zinc-700/30 font-mono">
                      {client.count}
                    </td>
                    <td className="px-3 py-1 text-center text-blue-400 border-r border-zinc-700/30 font-mono">
                      {client.multiplier}
                    </td>
                    <td className="px-3 py-1 text-center text-blue-400 border-r border-zinc-700/30 font-mono">
                      {client.banknifty_multiplier}
                    </td>
                    <td className="px-3 py-1 text-center text-blue-400 border-r border-zinc-700/30 font-mono">
                      {client.finnifty_multiplier}
                    </td>
                    <td className="px-3 py-1 text-center text-blue-400 border-r border-zinc-700/30 font-mono">
                      {client.midcpnifty_multiplier}
                    </td>
                    <td className="px-3 py-1 text-center text-blue-400 border-r border-zinc-700/30 font-mono">
                      {client.sensex_multiplier}
                    </td>
                    <td className="px-3 py-1 text-center text-blue-400 border-r border-zinc-700/30 font-mono">
                      {client.mcx_multiplier}
                    </td>
                    <td className="px-3 py-1 text-right text-zinc-300 border-r border-zinc-700/30 font-mono">
                      {formatCompactCurrency(client.balance)}
                    </td>
                    <td className="px-3 py-1 text-center text-zinc-300 border-r border-zinc-700/30 uppercase text-[10px]">
                      {client.broker}
                    </td>
                    <td
                      className={`px-3 py-1 text-right font-medium font-mono ${
                        client.pnl >= 0 ? "text-green-400" : "text-red-400"
                      }`}
                    >
                      {formatCurrency(client.pnl)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div> */}

        {/* Negative Positions */}
        {/* <div className="lg:col-span-1 bg-zinc-800 rounded border border-zinc-700">
          <div className="px-4 py-3 border-b border-zinc-700">
            <h2 className="text-sm font-semibold text-white">
              Negative Positions
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-max">
              <thead className="bg-zinc-700/50">
                <tr className="h-8">
                  <th className="px-3 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[32px]">
                    #
                    <SortIcon
                      column="index"
                      currentSort={negativePositionsSort}
                      onSort={() =>
                        handleSort(
                          "index",
                          negativePositionsSort,
                          setNegativePositionsSort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[120px]">
                    Symbol
                    <SortIcon
                      column="symbol"
                      currentSort={negativePositionsSort}
                      onSort={() =>
                        handleSort(
                          "symbol",
                          negativePositionsSort,
                          setNegativePositionsSort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-2 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">
                    Client ID
                    <SortIcon
                      column="client_id"
                      currentSort={negativePositionsSort}
                      onSort={() =>
                        handleSort(
                          "client_id",
                          negativePositionsSort,
                          setNegativePositionsSort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-2 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[100px]">
                    Client Name
                    <SortIcon
                      column="clientName"
                      currentSort={negativePositionsSort}
                      onSort={() =>
                        handleSort(
                          "clientName",
                          negativePositionsSort,
                          setNegativePositionsSort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[60px]">
                    Qty
                    <SortIcon
                      column="qty"
                      currentSort={negativePositionsSort}
                      onSort={() =>
                        handleSort(
                          "qty",
                          negativePositionsSort,
                          setNegativePositionsSort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-2 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">
                    Strategy
                    <SortIcon
                      column="strategy"
                      currentSort={negativePositionsSort}
                      onSort={() =>
                        handleSort(
                          "strategy",
                          negativePositionsSort,
                          setNegativePositionsSort
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-2 text-right text-zinc-300 font-medium min-w-[80px]">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedNegativePositions.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-3 py-8 text-center text-zinc-400 text-xl"
                    >
                      No Negative Positions Open
                    </td>
                  </tr>
                ) : (
                  sortedNegativePositions.map((position, index) => {
                    return (
                      <tr
                        key={position.id || index}
                        className="h-8 hover:bg-zinc-700/30 border-b border-zinc-700/30"
                      >
                        <td className="px-3 py-1 text-zinc-400 border-r border-zinc-700/30">
                          {index + 1}
                        </td>
                        <td className="px-3 py-1 border-r border-zinc-700/30">
                          <div className="text-white font-medium truncate">
                            {position.symbol}
                          </div>
                        </td>
                        <td className="px-3 py-1 text-center text-white border-r border-zinc-700/30 text-xs">
                          {position.client_id || "N/A"}
                        </td>
                        <td className="px-3 py-1 text-center text-white border-r border-zinc-700/30 text-xs truncate">
                          {position.clientName}
                        </td>
                        <td className="px-3 py-1 text-right text-red-500 border-r border-zinc-700/30">
                          {position.qty}
                        </td>
                        <td className="px-3 py-1 text-center text-zinc-300 border-r border-zinc-700/30 truncate">
                          {position.strategy || "N/A"}
                        </td>
                        <td className="px-3 py-1 text-right">
                          <Button
                            onClick={() => handleExitPosition(position)}
                            size="sm"
                            variant="destructive"
                            className="bg-red-900 hover:bg-red-800 text-red-200 text-xs px-3 py-1"
                          >
                            Close Position
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div> */}
      </div>
    </div>
  );
}

export default CompactDashboard;

// Compact Dashboar V2 without Sorting

// import React, { useMemo } from 'react';
// import { useGlobalPositions } from '../hooks/global-positions';
// import { Check, X } from 'lucide-react';
// import { Button } from "../components/ui/button";
// import apiClient from "../utils/apiClient";
// import toast from 'react-hot-toast';

// function CompactDashboard() {
//   const {
//     positions,
//     liveOrders,
//     totalPnL,
//     connectionStatus,
//     userData,
//     userDataLoading
//   } = useGlobalPositions();

//   const strategyOptions = [
//     "HE_PC5_MA10",
//     "HE_PC2_MA10",
//     "HHPC_PC2_MA10",
//     "HHPC_PC5_MA10",
//     "PDI_MDI_PC2_MA10",
//     "PDI_MDI_PC5_MA10",
//     "HE_PC5",
//     "HE_PC2",
//     "PDI_MA_PC2",
//     "PDI_MA_PC5",
//     "PDI_MDI_PC2",
//     "PDI_MDI_PC5",
//     "PDI_MA_PC2_MA10",
//     "PDI_MA_PC5_MA10",
//     "HHPC_PC2",
//     "HHPC_PC5",
//     "ORB_1",
//   ];

//   // Helper function to get user info by client_id
//   const getUserInfo = (clientId) => {
//     if (!clientId) return { name: 'N/A', multiplier: 0, balance: 0, broker: 'N/A' };
//     const user = userData[clientId];
//     if (!user) return { name: 'N/A', multiplier: 0, balance: 0, broker: 'N/A' };
//     return {
//       name: user.name || 'N/A',
//       multiplier: user.index_multiplier || 0,
//       balance: user.balance || 0,
//       broker: user.broker || 'N/A'
//     };
//   };

//   const handleExitPosition = (currentPositionData) => {
//     if (currentPositionData !== null) {
//       console.log("EXIT Position", currentPositionData);

//       let config = {
//         method: "post",
//         url: "https://godseye.server.sypto.xyz/exit_positions",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         data: JSON.stringify(currentPositionData),
//       };

//       apiClient
//         .request(config)
//         .then((response) => {
//           console.log("Exit Position response:", response.data);

//           if (response.data.status === 200) {
//             toast.success("Position Exit Successful !!", {
//               duration: 5000,
//             });
//           } else {
//             toast.error("Position Exit Failed !!", {
//               duration: 5000,
//             });
//           }
//         })
//         .catch((error) => {
//           console.log("Error in exit position request:", error);
//         });
//     } else {
//       alert("Please select a position for exit");
//     }
//   };

//   // Calculate metrics using ONLY backend-calculated data
//   const metrics = useMemo(() => {
//     console.log('=== STRATEGY COUNT DEBUG ===');
//     console.log('Total positions received:', positions.length);

//     const strategyCounts = {};
//     positions.forEach(p => {
//       const strategy = p.strategy || 'Manual';
//       strategyCounts[strategy] = (strategyCounts[strategy] || 0) + 1;
//     });

//     console.log('Strategy counts:', strategyCounts);
//     console.log('PDI_MA_PC2 positions:', positions.filter(p => p.strategy === 'PDI_MA_PC2').length);
//     console.log('PDI_MA_PC5 positions:', positions.filter(p => p.strategy === 'PDI_MA_PC5').length);

//     const zeroLtpPositions = positions.filter(p => parseFloat(p.ltp) === 0);
//     const zeroPLPositions = positions.filter(p => parseFloat(p.pl) === 0);
//     console.log('Positions with LTP=0:', zeroLtpPositions.length);
//     console.log('Positions with PL=0:', zeroPLPositions.length);
//     console.log('=== END DEBUG ===');

//     const totalPositions = positions.length;
//     const profitablePositions = positions.filter(p => (parseFloat(p.pl) || 0) > 0).length;
//     const lossPositions = positions.filter(p => (parseFloat(p.pl) || 0) < 0).length;

//     const totalInvestment = positions.reduce((sum, p) => {
//       const netAvg = parseFloat(p.net_avg) || 0;
//       const netQty = parseFloat(p.net_qty) || 0;
//       return sum + (netAvg * Math.abs(netQty));
//     }, 0);

//     const currentValue = positions.reduce((sum, p) => {
//       const currentPrice = parseFloat(p.ltp) || parseFloat(p.net_avg) || 0;
//       const netQty = parseFloat(p.net_qty) || 0;
//       return sum + (currentPrice * Math.abs(netQty));
//     }, 0);

//     const topPerformers = positions
//       .sort((a, b) => (parseFloat(b.pl) || 0) - (parseFloat(a.pl) || 0))
//       .slice(0, 10);

//     // Worst Performers (NEW)
//     const worstPerformers = positions
//       .filter(p => (parseFloat(p.pl) || 0) < 0)
//       .sort((a, b) => (parseFloat(a.pl) || 0) - (parseFloat(b.pl) || 0))
//       .slice(0, 10);

//     const strategies = positions.reduce((acc, p) => {
//       const strategy = p.strategy || 'N/A';
//       if (!acc[strategy]) {
//         acc[strategy] = { count: 0, pnl: 0 };
//       }
//       acc[strategy].count++;
//       acc[strategy].pnl += (parseFloat(p.pl) || 0);
//       return acc;
//     }, {});

//     const positionData = positions.reduce((acc, p) => {
//       const key = `${p.client_id}_${p.symbol}`;
//       const qty = Math.abs(parseFloat(p.net_qty) || 0);

//       if (!acc[key]) {
//         acc[key] = {
//           client_id: p.client_id,
//           symbol: p.symbol,
//           positionQty: 0,
//           sellOrderQty: 0
//         };
//       }

//       acc[key].positionQty += qty;
//       return acc;
//     }, {});

//     if (liveOrders && Array.isArray(liveOrders)) {
//       console.log('=== ORDER MATCHING DEBUG ===');
//       console.log('Sample position keys:', Object.keys(positionData).slice(0, 3));

//       const sellOrdersGrouped = liveOrders
//         .filter(order => order.side === -1)
//         .reduce((acc, order) => {
//           const key = `${order.clientId}_${order.symbol}`;
//           const orderQty = Math.abs(parseFloat(order.qty) || 0);

//           console.log(`Processing order: ${key} with qty ${orderQty}`);

//           if (!acc[key]) {
//             acc[key] = 0;
//           }
//           acc[key] += orderQty;
//           return acc;
//         }, {});

//       console.log('Grouped sell orders:', sellOrdersGrouped);
//       console.log('Position data keys:', Object.keys(positionData).slice(0, 5));

//       Object.entries(sellOrdersGrouped).forEach(([key, totalQty]) => {
//         if (positionData[key]) {
//           positionData[key].sellOrderQty = totalQty;
//           console.log(`✅ Matched: ${key} - Position: ${positionData[key].positionQty}, Orders: ${totalQty}`);
//         } else {
//           console.log(`❌ No position found for order key: ${key}`);
//         }
//       });

//       console.log('=== END ORDER MATCHING DEBUG ===');
//     }

//     const buySellData = Object.values(positionData)
//       .filter(item => item.positionQty > 0)
//       .map(item => ({
//         ...item,
//         isMatched: Math.abs(item.positionQty - item.sellOrderQty) === 0
//       }));

//     const negativePositions = [];
//     positions.forEach((position, index) => {
//       const hasNegativeQty = parseFloat(position.net_qty) < 0 || parseInt(position.side) === -1;

//       if (hasNegativeQty) {
//         negativePositions.push({
//           id: position.key || `${position.client_id}_${position.symbol}_${index}`,
//           ltp: position.ltp,
//           pl: position.pl,
//           side: position.side,
//           symbol: position.symbol,
//           clientIds: position.client_id ? [position.client_id] : [],
//           netQty: position.net_qty,
//           netAvg: position.net_avg,
//           strategy: position.strategy,
//           key: position.key || `${position.client_id}_${position.symbol}_${index}`,
//           client_id: position.client_id
//         });
//         console.log(`❌ Position has negative Qty: ${position.key || position.symbol}`);
//         console.log(
//           `   └─ Symbol: ${position.symbol} | Client: ${position.client_id} | Strategy: ${position.strategy} | NetQty: ${position.net_qty}`
//         );
//       }
//     });

//     return {
//       totalPositions,
//       profitablePositions,
//       lossPositions,
//       totalInvestment,
//       currentValue,
//       topPerformers,
//       worstPerformers,
//       strategies: Object.entries(strategies),
//       buySellData,
//       negativePositions
//     };
//   }, [positions, liveOrders]);

//   const formatCurrency = (amount) => {
//     if (amount === null || amount === undefined) return '₹0.00';
//     return new Intl.NumberFormat('en-IN', {
//       style: 'currency',
//       currency: 'INR',
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2
//     }).format(amount);
//   };

//   const formatCompactCurrency = (amount) => {
//     if (amount === null || amount === undefined) return '₹0';
//     return new Intl.NumberFormat('en-IN', {
//       style: 'currency',
//       currency: 'INR',
//       minimumFractionDigits: 0,
//       maximumFractionDigits: 0
//     }).format(amount);
//   };

//   return (
//     <div className="p-4 bg-zinc-900 min-h-screen">
//       {/* Compact Header */}
//       <div className="flex items-center justify-between mb-4">
//         <h1 className="text-xl font-bold text-white">Trading Dashboard</h1>
//         <div className="flex items-center space-x-3">
//           <div className={`w-2 h-2 rounded-full ${
//             connectionStatus === 'Connected' ? 'bg-green-400 animate-pulse' : 'bg-red-400'
//           }`}></div>
//           <span className="text-sm text-zinc-300">{connectionStatus}</span>
//           {userDataLoading && <span className="text-xs text-yellow-400">Loading users...</span>}
//         </div>
//       </div>

//       {/* Compact Metrics Grid */}
//       <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
//         <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
//           <div className="text-xs text-zinc-400 mb-1">Total Positions</div>
//           <div className="text-lg font-bold text-white">{metrics.totalPositions}</div>
//         </div>

//         <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
//           <div className="text-xs text-zinc-400 mb-1">Profitable</div>
//           <div className="text-lg font-bold text-green-400">{metrics.profitablePositions}</div>
//         </div>

//         <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
//           <div className="text-xs text-zinc-400 mb-1">Loss Making</div>
//           <div className="text-lg font-bold text-red-400">{metrics.lossPositions}</div>
//         </div>

//         <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
//           <div className="text-xs text-zinc-400 mb-1">Total P&L</div>
//           <div className={`text-lg font-bold ${totalPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
//             {formatCurrency(totalPnL)}
//           </div>
//         </div>

//         <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
//           <div className="text-xs text-zinc-400 mb-1">Investment</div>
//           <div className="text-lg font-bold text-white">{formatCompactCurrency(metrics.totalInvestment)}</div>
//         </div>

//         <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
//           <div className="text-xs text-zinc-400 mb-1">Current Value</div>
//           <div className="text-lg font-bold text-white">{formatCompactCurrency(metrics.currentValue)}</div>
//         </div>
//       </div>

//       {/* Two Column Layout */}
//       <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

//         {/* Top Performers Table - Takes 2 columns */}
//         <div className=" bg-zinc-800 rounded border border-zinc-700">
//           <div className="px-4 py-3 border-b border-zinc-700">
//             <h2 className="text-sm font-semibold text-white">Top Performers</h2>
//           </div>

//           <div className="overflow-x-auto">
//             <table className="w-full text-xs min-w-max">
//               <thead className="bg-zinc-700/50">
//                 <tr className="h-8">
//                   <th className="px-3 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[32px]">#</th>
//                   <th className="px-3 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[120px] ">Symbol</th>
//                   <th className="px-3 py-2 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[60px]">Side</th>
//                   <th className="px-3 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">Qty</th>
//                   <th className="px-3 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">Avg Price</th>
//                   <th className="px-3 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">LTP</th>
//                   <th className="px-3 py-2 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">Strategy</th>
//                   <th className="px-3 py-2 text-right text-zinc-300 font-medium min-w-[100px]">P&L</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {metrics.topPerformers.map((position, index) => {
//                   const currentPrice = parseFloat(position.ltp) || parseFloat(position.net_avg) || 0;
//                   const netAvg = parseFloat(position.net_avg) || 0;
//                   const priceChange = currentPrice - netAvg;
//                   const priceChangePercent = netAvg > 0 ? ((priceChange / netAvg) * 100).toFixed(1) : '0.0';

//                   return (
//                     <tr key={position.id || index} className="h-8 hover:bg-zinc-700/30 border-b border-zinc-700/30">
//                       <td className="px-3 py-1 text-zinc-400 border-r border-zinc-700/30">{index + 1}</td>
//                       <td className="px-3 py-1 border-r border-zinc-700/30">
//                         <div className="text-white font-medium truncate">
//                           {(position.symbol || '')}
//                         </div>
//                       </td>
//                       <td className={`px-3 py-1 text-center border-r border-zinc-700/30 font-medium ${
//                         parseInt(position.side) === 1 && position.net_qty > 0 ? 'text-green-400' : 'text-red-400'
//                       }`}>
//                         {parseInt(position.side) === 1 && position.net_qty > 0 ? 'BUY' : 'SELL'}
//                       </td>
//                       <td className="px-3 py-1 text-right text-white border-r border-zinc-700/30">
//                         {position.net_qty?.toLocaleString()}
//                       </td>
//                       <td className="px-3 py-1 text-right text-white border-r border-zinc-700/30">
//                         ₹{netAvg.toFixed(2)}
//                       </td>
//                       <td className="px-3 py-1 text-right text-white border-r border-zinc-700/30">
//                         ₹{currentPrice.toFixed(2)}
//                       </td>
//                       <td className="px-3 py-1 text-center text-zinc-300 border-r border-zinc-700/30 truncate">
//                         {position.strategy || 'Manual'}
//                       </td>
//                       <td className={`px-3 py-1 text-right font-medium font-mono ${
//                         (parseFloat(position.pl) || 0) >= 0 ? 'text-green-400' : 'text-red-400'
//                       }`}>
//                         {formatCurrency(parseFloat(position.pl) || 0)}
//                       </td>
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           </div>
//         </div>

//         {/* Worst Performers - NEW (replaces All Loss Positions) */}
//         <div className=" bg-zinc-800 rounded border border-zinc-700">
//           <div className="px-4 py-3 border-b border-zinc-700">
//             <h2 className="text-sm font-semibold text-white">Worst Performers</h2>
//           </div>

//           <div className="overflow-x-auto">
//             <table className="w-full text-xs min-w-max">
//               <thead className="bg-zinc-700 sticky top-0">
//                 <tr className="h-8">
//                   <th className="px-3 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[32px]">#</th>
//                   <th className="px-3 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[120px] ">Symbol</th>
//                   <th className="px-3 py-2 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[60px]">Side</th>
//                   <th className="px-3 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">Qty</th>
//                   <th className="px-3 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">Avg Price</th>
//                   <th className="px-3 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">LTP</th>
//                   <th className="px-3 py-2 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">Strategy</th>
//                   <th className="px-3 py-2 text-right text-zinc-300 font-medium min-w-[100px]">P&L</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {metrics.worstPerformers.length === 0 ? (
//                   <tr>
//                     <td colSpan="8" className="px-3 py-8 text-center text-zinc-400 text-xl">
//                       No Loss Positions
//                     </td>
//                   </tr>
//                 ) : (
//                   metrics.worstPerformers.map((position, index) => {
//                     const currentPrice = parseFloat(position.ltp) || parseFloat(position.net_avg) || 0;
//                     const netAvg = parseFloat(position.net_avg) || 0;
//                     const priceChange = currentPrice - netAvg;
//                     const priceChangePercent = netAvg > 0 ? ((priceChange / netAvg) * 100).toFixed(1) : '0.0';

//                     return (
//                       <tr key={position.id || index} className="h-8 hover:bg-zinc-700/30 border-b border-zinc-700/30">
//                         <td className="px-3 py-1 text-zinc-400 border-r border-zinc-700/30">{index + 1}</td>
//                         <td className="px-3 py-1 border-r border-zinc-700/30">
//                           <div className="text-white font-medium truncate">
//                             {(position.symbol || '')}
//                           </div>
//                         </td>
//                         <td className={`px-3 py-1 text-center border-r border-zinc-700/30 font-medium ${
//                         parseInt(position.side) === 1 && position.net_qty > 0 ? 'text-green-400' : 'text-red-400'
//                       }`}>
//                         {parseInt(position.side) === 1 && position.net_qty > 0 ? 'BUY' : 'SELL'}
//                       </td>
//                       <td className="px-3 py-1 text-right text-white border-r border-zinc-700/30">
//                         {position.net_qty?.toLocaleString()}
//                       </td>
//                       <td className="px-3 py-1 text-right text-white border-r border-zinc-700/30">
//                         ₹{netAvg.toFixed(2)}
//                       </td>
//                       <td className="px-3 py-1 text-right text-white border-r border-zinc-700/30">
//                         ₹{currentPrice.toFixed(2)}
//                       </td>
//                       <td className="px-3 py-1 text-center text-zinc-300 border-r border-zinc-700/30 truncate">
//                         {position.strategy || 'Manual'}
//                       </td>
//                       <td className={`px-3 py-1 text-right font-medium font-mono ${
//                         (parseFloat(position.pl) || 0) >= 0 ? 'text-green-400' : 'text-red-400'
//                       }`}>
//                         {formatCurrency(parseFloat(position.pl) || 0)}
//                       </td>
//                       </tr>
//                     );
//                   })
//                 )}
//               </tbody>
//             </table>
//           </div>
//         </div>

//       </div>

//       {/* Three Column Layout for Additional Tables */}
//       <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">

//         {/* Position vs Sell Order Qty Comparison - UPDATED with Client Name */}
//         <div className="bg-zinc-800 rounded border border-zinc-700">
//           <div className="px-4 py-3 border-b border-zinc-700">
//             <h2 className="text-sm font-semibold text-white">Position vs Sell Order Qty</h2>
//             <div className="text-xs text-zinc-400 mt-1">
//               Covered: {metrics.buySellData.filter(item => item.isMatched).length} / {metrics.buySellData.length}
//               {!liveOrders && <span className="text-yellow-400 ml-2">(Orders data not available)</span>}
//             </div>
//           </div>

//           <div className="overflow-x-auto max-h-96">
//             <table className="w-full text-xs">
//               <thead className="bg-zinc-700 sticky top-0">
//                 <tr className="h-8">
//                   <th className="px-2 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30 w-8">#</th>
//                   <th className="px-2 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30">Client ID</th>
//                   <th className="px-2 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30">Client Name</th>
//                   <th className="px-2 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30">Symbol</th>
//                   <th className="px-2 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30">Position Qty</th>
//                   <th className="px-2 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30">Sell Qty</th>
//                   <th className="px-2 py-2 text-center text-zinc-300 font-medium">Equal?</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {metrics.buySellData.map((item, index) => {
//                   const userInfo = getUserInfo(item.client_id);
//                   return (
//                     <tr key={index} className={`h-8 hover:bg-zinc-700/30 border-b border-zinc-700/30 ${
//                       !item.isMatched ? 'bg-red-900/20' : ''
//                     }`}>
//                       <td className="px-2 py-1 text-zinc-400 border-r border-zinc-700/30">{index + 1}</td>
//                       <td className="px-2 py-1 text-white border-r border-zinc-700/30 truncate text-xs">
//                         {item.client_id || 'N/A'}
//                       </td>
//                       <td className="px-2 py-1 text-white border-r border-zinc-700/30 truncate text-xs">
//                         {userInfo.name}
//                       </td>
//                       <td className="px-2 py-1 text-white border-r border-zinc-700/30 truncate">
//                         {(item.symbol || '').replace('NSE:', '')}
//                       </td>
//                       <td className="px-2 py-1 text-right text-blue-400 border-r border-zinc-700/30 font-mono">
//                         {item.positionQty.toFixed(0)}
//                       </td>
//                       <td className="px-2 py-1 text-right text-orange-400 border-r border-zinc-700/30 font-mono">
//                         {item.sellOrderQty.toFixed(0)}
//                       </td>
//                       <td className="px-2 py-1 text-center">
//                         {item.isMatched ? (
//                           <Check className="w-4 h-4 text-green-400 mx-auto" />
//                         ) : (
//                           <X className="w-4 h-4 text-red-400 mx-auto" />
//                         )}
//                       </td>
//                     </tr>
//                   );
//                 })}
//                 {metrics.buySellData.length === 0 && (
//                   <tr>
//                     <td colSpan="7" className="px-4 py-8 text-center text-zinc-400">
//                       {!liveOrders ? 'Orders data not available' : 'No positions found'}
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>
//         </div>

//         {/* Strategy Breakdown - Takes 1 column */}
//         <div className="bg-zinc-800 rounded border border-zinc-700">
//           <div className="px-4 py-3 border-b border-zinc-700">
//             <h2 className="text-sm font-semibold text-white">Strategy Breakdown</h2>
//             <div className="text-xs text-zinc-400 mt-1">Total: {metrics.totalPositions} positions</div>
//           </div>

//           <div className="p-4 space-y-3">
//             {metrics.strategies.map(([strategy, data], index) => (
//               <div key={index} className="flex items-center justify-between">
//                 <div className="flex items-center space-x-2 flex-1 min-w-0">
//                   <div className="text-sm text-white font-medium truncate">{strategy}</div>
//                   <div className="text-xs text-zinc-400 whitespace-nowrap">
//                     {data.count} positions
//                   </div>
//                 </div>
//                 <div className={`text-sm font-semibold font-mono ml-2 ${data.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
//                   {formatCurrency(data.pnl)}
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>

//         {/* Client Summary - UPDATED with new columns */}
//         <div className="bg-zinc-800 rounded border border-zinc-700">
//           <div className="px-4 py-3 border-b border-zinc-700">
//             <h2 className="text-sm font-semibold text-white">Client Summary</h2>
//           </div>

//           <div className="overflow-x-auto max-h-96">
//             <table className="w-full text-xs min-w-max">
//               <thead className="bg-zinc-700/50">
//                 <tr className="h-7">
//                   <th className="px-3 py-1 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">Client ID</th>
//                   <th className="px-3 py-1 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[120px]">Client Name</th>
//                   <th className="px-3 py-1 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[60px]">Positions</th>
//                   <th className="px-3 py-1 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[70px]">Multiplier</th>
//                   <th className="px-3 py-1 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">Balance</th>
//                   <th className="px-3 py-1 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[60px]">Broker</th>
//                   <th className="px-3 py-1 text-right text-zinc-300 font-medium min-w-[100px]">P&L</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {Object.entries(
//                   positions.reduce((acc, p) => {
//                     const client = p.client_id || 'N/A';
//                     if (!acc[client]) {
//                       acc[client] = { count: 0, pnl: 0 };
//                     }
//                     acc[client].count++;
//                     acc[client].pnl += (parseFloat(p.pl) || 0);
//                     return acc;
//                   }, {})
//                 ).map(([client, data], index) => {
//                   const userInfo = getUserInfo(client);
//                   return (
//                     <tr key={index} className="h-7 hover:bg-zinc-700/30 border-b border-zinc-700/30">
//                       <td className="px-3 py-1 text-white border-r border-zinc-700/30 truncate">
//                         {client}
//                       </td>
//                       <td className="px-3 py-1 text-white border-r border-zinc-700/30 truncate">
//                         {userInfo.name}
//                       </td>
//                       <td className="px-3 py-1 text-center text-zinc-300 border-r border-zinc-700/30 font-mono">
//                         {data.count}
//                       </td>
//                       <td className="px-3 py-1 text-center text-blue-400 border-r border-zinc-700/30 font-mono">
//                         {userInfo.multiplier}x
//                       </td>
//                       <td className="px-3 py-1 text-right text-zinc-300 border-r border-zinc-700/30 font-mono">
//                         {formatCompactCurrency(userInfo.balance)}
//                       </td>
//                       <td className="px-3 py-1 text-center text-zinc-300 border-r border-zinc-700/30 uppercase text-[10px]">
//                         {userInfo.broker}
//                       </td>
//                       <td className={`px-3 py-1 text-right font-medium font-mono ${
//                         data.pnl >= 0 ? 'text-green-400' : 'text-red-400'
//                       }`}>
//                         {formatCurrency(data.pnl)}
//                       </td>
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           </div>
//         </div>

//         {/* Negative Positions - UPDATED with Client Name */}
//         <div className="lg:col-span-1 bg-zinc-800 rounded border border-zinc-700">
//           <div className="px-4 py-3 border-b border-zinc-700">
//             <h2 className="text-sm font-semibold text-white">Negative Positions</h2>
//           </div>

//           <div className="overflow-x-auto">
//             <table className="w-full text-xs min-w-max">
//               <thead className="bg-zinc-700/50">
//                 <tr className="h-8">
//                   <th className="px-3 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[32px]">#</th>
//                   <th className="px-3 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[120px]">Symbol</th>
//                   <th className="px-3 py-2 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">Client ID</th>
//                   <th className="px-3 py-2 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[100px]">Client Name</th>
//                   <th className="px-3 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[60px]">Qty</th>
//                   <th className="px-3 py-2 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">Strategy</th>
//                   <th className="px-3 py-2 text-right text-zinc-300 font-medium min-w-[80px]">Action</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {metrics.negativePositions.length === 0 ? (
//                   <tr>
//                     <td colSpan="7" className="px-3 py-8 text-center text-zinc-400 text-xl">
//                       No Negative Positions Open
//                     </td>
//                   </tr>
//                 ) : (
//                   metrics.negativePositions.map((position, index) => {
//                     const userInfo = getUserInfo(position.client_id);
//                     return (
//                       <tr key={position.id || index} className="h-8 hover:bg-zinc-700/30 border-b border-zinc-700/30">
//                         <td className="px-3 py-1 text-zinc-400 border-r border-zinc-700/30">{index + 1}</td>
//                         <td className="px-3 py-1 border-r border-zinc-700/30">
//                           <div className="text-white font-medium truncate">
//                             {position.symbol}
//                           </div>
//                         </td>
//                         <td className="px-3 py-1 text-center text-white border-r border-zinc-700/30 text-xs">
//                           {position.client_id || 'N/A'}
//                         </td>
//                         <td className="px-3 py-1 text-center text-white border-r border-zinc-700/30 text-xs truncate">
//                           {userInfo.name}
//                         </td>
//                         <td className="px-3 py-1 text-right text-red-500 border-r border-zinc-700/30">
//                           {position.netQty}
//                         </td>
//                         <td className="px-3 py-1 text-center text-zinc-300 border-r border-zinc-700/30 truncate">
//                           {position.strategy || "N/A"}
//                         </td>
//                         <td className="px-3 py-1 text-right">
//                           <Button
//                             onClick={() => handleExitPosition(position)}
//                             size="sm"
//                             variant="destructive"
//                             className="bg-red-900 hover:bg-red-800 text-red-200 text-xs px-3 py-1"
//                           >
//                             Close Position
//                           </Button>
//                         </td>
//                       </tr>
//                     );
//                   })
//                 )}
//               </tbody>
//             </table>
//           </div>
//         </div>

//       </div>
//     </div>
//   );
// }

// export default CompactDashboard;

// import React, { useMemo } from 'react';
// import { useGlobalPositions } from '../hooks/global-positions';
// import { Check, X } from 'lucide-react';
// import { Button } from "../components/ui/button";
// import apiClient from "../utils/apiClient";
// import toast from 'react-hot-toast';
// import moment from "moment-timezone";

// function CompactDashboard() {
//   const {
//     positions,
//     liveOrders,
//     totalPnL,
//     connectionStatus
//   } = useGlobalPositions();

//     // Strategy options for dropdown
//   const strategyOptions = [
//     "HE_PC5_MA10",
//     "HE_PC2_MA10",
//     "HHPC_PC2_MA10",
//     "HHPC_PC5_MA10",
//     "PDI_MDI_PC2_MA10",
//     "PDI_MDI_PC5_MA10",
//     "HE_PC5",
//     "HE_PC2",
//     "PDI_MA_PC2",
//     "PDI_MA_PC5",
//     "PDI_MDI_PC2",
//     "PDI_MDI_PC5",
//     "PDI_MA_PC2_MA10",
//     "PDI_MA_PC5_MA10",
//     "HHPC_PC2",
//     "HHPC_PC5",
//     "ORB_1",
//   ];

//    const handleExitPosition = (currentPositionData) => {
//       if (currentPositionData !== null) {
//         console.log("EXIT Position", currentPositionData);

//       let config = {
//           method: "post",
//           url: "https://godseye.server.sypto.xyz/exit_positions",
//           headers: {
//             "Content-Type": "application/json",
//           },
//           data: JSON.stringify(currentPositionData),
//         };

//         apiClient
//           .request(config)
//           .then((response) => {
//             console.log("Exit Position response:", response.data);

//             if (response.data.status === 200) {
//               toast.success("Position Exit Successful !!", {
//                 duration: 5000,
//               });
//             } else {
//               toast.error("Position Exit Failed !!", {
//                 duration: 5000,
//               });
//             }

//           })
//           .catch((error) => {
//             console.log("Error in exit position request:", error);

//           });
//       } else {
//         alert("Please select a position for exit");
//       }
//     };

//   // Calculate metrics using ONLY backend-calculated data
//   const metrics = useMemo(() => {
//     // ✅ DEBUG: Log the actual positions count to help diagnose the issue
//     console.log('=== STRATEGY COUNT DEBUG ===');
//     console.log('Total positions received:', positions.length);

//     // Count each strategy
//     const strategyCounts = {};
//     positions.forEach(p => {
//       const strategy = p.strategy || 'Manual';
//       strategyCounts[strategy] = (strategyCounts[strategy] || 0) + 1;
//     });

//     console.log('Strategy counts:', strategyCounts);
//     console.log('PDI_MA_PC2 positions:', positions.filter(p => p.strategy === 'PDI_MA_PC2').length);
//     console.log('PDI_MA_PC5 positions:', positions.filter(p => p.strategy === 'PDI_MA_PC5').length);

//     // Check for positions with zero values
//     const zeroLtpPositions = positions.filter(p => parseFloat(p.ltp) === 0);
//     const zeroPLPositions = positions.filter(p => parseFloat(p.pl) === 0);
//     console.log('Positions with LTP=0:', zeroLtpPositions.length);
//     console.log('Positions with PL=0:', zeroPLPositions.length);
//     console.log('=== END DEBUG ===');

//     const totalPositions = positions.length;
//     const profitablePositions = positions.filter(p => (parseFloat(p.pl) || 0) > 0).length;
//     const lossPositions = positions.filter(p => (parseFloat(p.pl) || 0) < 0).length;

//     // ✅ Use backend-calculated LTP and P&L - no frontend calculations
//     const totalInvestment = positions.reduce((sum, p) => {
//       const netAvg = parseFloat(p.net_avg) || 0;
//       const netQty = parseFloat(p.net_qty) || 0;
//       return sum + (netAvg * Math.abs(netQty));
//     }, 0);

//     const currentValue = positions.reduce((sum, p) => {
//       const currentPrice = parseFloat(p.ltp) || parseFloat(p.net_avg) || 0; // Use backend LTP
//       const netQty = parseFloat(p.net_qty) || 0;
//       return sum + (currentPrice * Math.abs(netQty));
//     }, 0);

//     const topPerformers = positions
//       .sort((a, b) => (parseFloat(b.pl) || 0) - (parseFloat(a.pl) || 0))
//       .slice(0, 10);

//     // ✅ FIXED: Include ALL positions in strategy count, even those with ltp=0 or pl=0
//     const strategies = positions.reduce((acc, p) => {
//       const strategy = p.strategy || 'N/A';
//       if (!acc[strategy]) {
//         acc[strategy] = { count: 0, pnl: 0 };
//       }
//       // Count ALL positions for each strategy
//       acc[strategy].count++;
//       // Sum P&L (including zero values)
//       acc[strategy].pnl += (parseFloat(p.pl) || 0);
//       return acc;
//     }, {});

//     // ✅ FIXED: Calculate Position vs Sell Order Qty comparison
//     // First, get all positions with their quantities (using correct field names)
//     const positionData = positions.reduce((acc, p) => {
//       const key = `${p.client_id}_${p.symbol}`;
//       const qty = Math.abs(parseFloat(p.net_qty) || 0); // ✅ FIXED: Use netQty not net_qty

//       if (!acc[key]) {
//         acc[key] = {
//           client_id: p.client_id,
//           symbol: p.symbol,
//           positionQty: 0,
//           sellOrderQty: 0
//         };
//       }

//       acc[key].positionQty += qty;
//       return acc;
//     }, {});

//     // ✅ FIXED: Group sell orders by client-symbol (using correct field names)
//     if (liveOrders && Array.isArray(liveOrders)) {
//       console.log('=== ORDER MATCHING DEBUG ===');
//       console.log('Sample position keys:', Object.keys(positionData).slice(0, 3));

//       // Group sell orders by client-symbol combination
//       const sellOrdersGrouped = liveOrders
//         .filter(order => order.side === -1) // Only sell orders
//         .reduce((acc, order) => {
//           const key = `${order.clientId}_${order.symbol}`; // ✅ FIXED: Use clientId not client_id
//           const orderQty = Math.abs(parseFloat(order.qty) || 0);

//           console.log(`Processing order: ${key} with qty ${orderQty}`);

//           if (!acc[key]) {
//             acc[key] = 0;
//           }
//           acc[key] += orderQty;
//           return acc;
//         }, {});

//       console.log('Grouped sell orders:', sellOrdersGrouped);
//       console.log('Position data keys:', Object.keys(positionData).slice(0, 5));

//       // Now add the grouped sell order quantities to positionData
//       Object.entries(sellOrdersGrouped).forEach(([key, totalQty]) => {
//         if (positionData[key]) {
//           positionData[key].sellOrderQty = totalQty;
//           console.log(`✅ Matched: ${key} - Position: ${positionData[key].positionQty}, Orders: ${totalQty}`);
//         } else {
//           console.log(`❌ No position found for order key: ${key}`);
//         }
//       });

//       console.log('=== END ORDER MATCHING DEBUG ===');
//     }

//     const buySellData = Object.values(positionData)
//       .filter(item => item.positionQty > 0) // Only show positions with actual quantities
//       .map(item => ({
//         ...item,
//         isMatched: Math.abs(item.positionQty - item.sellOrderQty) === 0 // Position qty matches sell order qty
//       }));

//     // ✅ NEW: Calculate Negative Positions
//     const negativePositions = [];
//     positions.forEach((position, index) => {
//       const hasNegativeQty = parseFloat(position.net_qty) < 0 || parseInt(position.side) === -1;

//       if (hasNegativeQty) {
//         negativePositions.push({
//           id: position.key || `${position.client_id}_${position.symbol}_${index}`,
//           ltp: position.ltp,
//           pl: position.pl,
//           side: position.side,
//           symbol: position.symbol,
//           clientIds: position.client_id ? [position.client_id] : [],
//           netQty: position.net_qty,
//           netAvg: position.net_avg,
//           strategy: position.strategy,
//           key: position.key || `${position.client_id}_${position.symbol}_${index}`,
//           client_id: position.client_id // Keep original field name for compatibility
//         });
//         console.log(`❌ Position has negative Qty: ${position.key || position.symbol}`);
//         console.log(
//           `   └─ Symbol: ${position.symbol} | Client: ${position.client_id} | Strategy: ${position.strategy} | NetQty: ${position.net_qty}`
//         );
//       }
//     });

//     return {
//       totalPositions,
//       profitablePositions,
//       lossPositions,
//       totalInvestment,
//       currentValue,
//       topPerformers,
//       strategies: Object.entries(strategies),
//       buySellData,
//       negativePositions
//     };
//   }, [positions]); // ✅ Only depend on positions

//   const formatCurrency = (amount) => {
//     if (amount === null || amount === undefined) return '₹0.00';
//     return new Intl.NumberFormat('en-IN', {
//       style: 'currency',
//       currency: 'INR',
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2
//     }).format(amount);
//   };

//   const formatCompactCurrency = (amount) => {
//     if (amount === null || amount === undefined) return '₹0';
//     return new Intl.NumberFormat('en-IN', {
//       style: 'currency',
//       currency: 'INR',
//       minimumFractionDigits: 0,
//       maximumFractionDigits: 0
//     }).format(amount);
//   };

//   return (
//     <div className="p-4 bg-zinc-900 min-h-screen">
//       {/* Compact Header */}
//       <div className="flex items-center justify-between mb-4">
//         <h1 className="text-xl font-bold text-white">Trading Dashboard</h1>
//         <div className="flex items-center space-x-3">
//           <div className={`w-2 h-2 rounded-full ${
//             connectionStatus === 'Connected' ? 'bg-green-400 animate-pulse' : 'bg-red-400'
//           }`}></div>
//           <span className="text-sm text-zinc-300">{connectionStatus}</span>
//         </div>
//       </div>

//       {/* Compact Metrics Grid */}
//       <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
//         <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
//           <div className="text-xs text-zinc-400 mb-1">Total Positions</div>
//           <div className="text-lg font-bold text-white">{metrics.totalPositions}</div>
//         </div>

//         <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
//           <div className="text-xs text-zinc-400 mb-1">Profitable</div>
//           <div className="text-lg font-bold text-green-400">{metrics.profitablePositions}</div>
//         </div>

//         <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
//           <div className="text-xs text-zinc-400 mb-1">Loss Making</div>
//           <div className="text-lg font-bold text-red-400">{metrics.lossPositions}</div>
//         </div>

//         <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
//           <div className="text-xs text-zinc-400 mb-1">Total P&L</div>
//           <div className={`text-lg font-bold ${totalPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
//             {formatCurrency(totalPnL)}
//           </div>
//         </div>

//         <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
//           <div className="text-xs text-zinc-400 mb-1">Investment</div>
//           <div className="text-lg font-bold text-white">{formatCompactCurrency(metrics.totalInvestment)}</div>
//         </div>

//         <div className="bg-zinc-800 p-3 rounded border border-zinc-700">
//           <div className="text-xs text-zinc-400 mb-1">Current Value</div>
//           <div className="text-lg font-bold text-white">{formatCompactCurrency(metrics.currentValue)}</div>
//         </div>
//       </div>

//       {/* Two Column Layout */}
//       <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

//         {/* Top Performers Table - Takes 2 columns */}
//         <div className="lg:col-span-2 bg-zinc-800 rounded border border-zinc-700">
//           <div className="px-4 py-3 border-b border-zinc-700">
//             <h2 className="text-sm font-semibold text-white">Top Performers</h2>
//           </div>

//           <div className="overflow-x-auto">
//             <table className="w-full text-xs min-w-max">
//               <thead className="bg-zinc-700/50">
//                 <tr className="h-8">
//                   <th className="px-3 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[32px]">#</th>
//                   <th className="px-3 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[120px]">Symbol</th>
//                   <th className="px-3 py-2 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[60px]">Side</th>
//                   <th className="px-3 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">Qty</th>
//                   <th className="px-3 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">Avg Price</th>
//                   <th className="px-3 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">LTP</th>
//                   <th className="px-3 py-2 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">Strategy</th>
//                   <th className="px-3 py-2 text-right text-zinc-300 font-medium min-w-[100px]">P&L</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {metrics.topPerformers.map((position, index) => {
//                   // ✅ Use backend-calculated LTP - no frontend calculations
//                   const currentPrice = parseFloat(position.ltp) || parseFloat(position.net_avg) || 0;
//                   const netAvg = parseFloat(position.net_avg) || 0;
//                   const priceChange = currentPrice - netAvg;
//                   const priceChangePercent = netAvg > 0 ? ((priceChange / netAvg) * 100).toFixed(1) : '0.0';

//                   return (
//                     <tr key={position.id || index} className="h-8 hover:bg-zinc-700/30 border-b border-zinc-700/30">
//                       <td className="px-3 py-1 text-zinc-400 border-r border-zinc-700/30">{index + 1}</td>
//                       <td className="px-3 py-1 border-r border-zinc-700/30">
//                         <div className="text-white font-medium truncate">
//                           {(position.symbol || '').replace('NSE:', '')}
//                         </div>
//                         <div className={`text-[10px] ${priceChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
//                           {priceChangePercent}%
//                         </div>
//                       </td>
//                       <td className={`px-3 py-1 text-center border-r border-zinc-700/30 font-medium ${
//                         parseInt(position.side) === 1 ? 'text-green-400' : 'text-red-400'
//                       }`}>
//                         {parseInt(position.side) === 1 ? 'BUY' : 'SELL'}
//                       </td>
//                       <td className="px-3 py-1 text-right text-white border-r border-zinc-700/30">
//                         {Math.abs(parseFloat(position.net_qty) || 0).toLocaleString()}
//                       </td>
//                       <td className="px-3 py-1 text-right text-white border-r border-zinc-700/30">
//                         ₹{netAvg.toFixed(0)}
//                       </td>
//                       <td className="px-3 py-1 text-right text-white border-r border-zinc-700/30">
//                         ₹{currentPrice.toFixed(0)}
//                       </td>
//                       <td className="px-3 py-1 text-center text-zinc-300 border-r border-zinc-700/30 truncate">
//                         {position.strategy || 'Manual'}
//                       </td>
//                       <td className={`px-3 py-1 text-right font-medium font-mono ${
//                         (parseFloat(position.pl) || 0) >= 0 ? 'text-green-400' : 'text-red-400'
//                       }`}>
//                         {formatCurrency(parseFloat(position.pl) || 0)}
//                       </td>
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           </div>
//         </div>

//         {/* Strategy Breakdown - Takes 1 column */}
//         <div className="bg-zinc-800 rounded border border-zinc-700">
//           <div className="px-4 py-3 border-b border-zinc-700">
//             <h2 className="text-sm font-semibold text-white">Strategy Breakdown</h2>
//             {/* ✅ DEBUG: Show total positions count */}
//             <div className="text-xs text-zinc-400 mt-1">Total: {metrics.totalPositions} positions</div>
//           </div>

//           <div className="p-4 space-y-3">
//             {metrics.strategies.map(([strategy, data], index) => (
//               <div key={index} className="flex items-center justify-between">
//                 <div className="flex items-center space-x-2 flex-1 min-w-0">
//                   <div className="text-sm text-white font-medium truncate">{strategy}</div>
//                   <div className="text-xs text-zinc-400 whitespace-nowrap">
//                     {data.count} positions
//                     {/* ✅ DEBUG: Highlight PDI strategies */}
//                   </div>
//                 </div>
//                 <div className={`text-sm font-semibold font-mono ml-2 ${data.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
//                   {formatCurrency(data.pnl)}
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       </div>

//       {/* Three Column Layout for Additional Tables */}
//       <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">

//         {/* Position vs Sell Order Qty Comparison - UPDATED TABLE */}
//         <div className="bg-zinc-800 rounded border border-zinc-700">
//           <div className="px-4 py-3 border-b border-zinc-700">
//             <h2 className="text-sm font-semibold text-white">Position vs Sell Order Qty</h2>
//             <div className="text-xs text-zinc-400 mt-1">
//               Covered: {metrics.buySellData.filter(item => item.isMatched).length} / {metrics.buySellData.length}
//               {!liveOrders && <span className="text-yellow-400 ml-2">(Orders data not available)</span>}
//             </div>
//           </div>

//           <div className="overflow-x-auto max-h-96">
//             <table className="w-full text-xs">
//               <thead className="bg-zinc-700 sticky top-0">
//                 <tr className="h-8">
//                   <th className="px-2 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30 w-8">#</th>
//                   <th className="px-2 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30">Client</th>
//                   <th className="px-2 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30">Symbol</th>
//                   <th className="px-2 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30">Position Qty</th>
//                   <th className="px-2 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30">Sell Qty</th>
//                   <th className="px-2 py-2 text-center text-zinc-300 font-medium">Equal?</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {metrics.buySellData.map((item, index) => (
//                   <tr key={index} className={`h-8 hover:bg-zinc-700/30 border-b border-zinc-700/30 ${
//                     !item.isMatched ? 'bg-red-900/20' : ''
//                   }`}>
//                     <td className="px-2 py-1 text-zinc-400 border-r border-zinc-700/30">{index + 1}</td>
//                     <td className="px-2 py-1 text-white border-r border-zinc-700/30 truncate text-xs">
//                       {item.client_id || 'N/A'}
//                     </td>
//                     <td className="px-2 py-1 text-white border-r border-zinc-700/30 truncate">
//                       {(item.symbol || '').replace('NSE:', '')}
//                     </td>
//                     <td className="px-2 py-1 text-right text-blue-400 border-r border-zinc-700/30 font-mono">
//                       {item.positionQty.toFixed(0)}
//                     </td>
//                     <td className="px-2 py-1 text-right text-orange-400 border-r border-zinc-700/30 font-mono">
//                       {item.sellOrderQty.toFixed(0)}
//                     </td>
//                     <td className="px-2 py-1 text-center">
//                       {item.isMatched ? (
//                         <Check className="w-4 h-4 text-green-400 mx-auto" />
//                       ) : (
//                         <X className="w-4 h-4 text-red-400 mx-auto" />
//                       )}
//                     </td>
//                   </tr>
//                 ))}
//                 {metrics.buySellData.length === 0 && (
//                   <tr>
//                     <td colSpan="6" className="px-4 py-8 text-center text-zinc-400">
//                       {!liveOrders ? 'Orders data not available' : 'No positions found'}
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>
//         </div>

//         {/* Top 10 Loss Positions */}
//         <div className="bg-zinc-800 rounded border border-zinc-700">
//           <div className="px-4 py-3 border-b border-zinc-700">
//             <h2 className="text-sm font-semibold text-white">All Loss Positions</h2>
//           </div>

//           <div className="overflow-x-auto max-h-96">
//             <table className="w-full text-xs min-w-max">
//               <thead className="bg-zinc-700 sticky top-0">
//                 <tr className="h-7">
//                   <th className="px-3 py-1 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[120px]">Symbol</th>
//                   <th className="px-3 py-1 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[60px]">Qty</th>
//                   <th className="px-3 py-1 text-right text-zinc-300 font-medium min-w-[100px]">P&L</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {positions
//                   .filter(p => (parseFloat(p.pl) || 0) < 0)
//                   .sort((a, b) => (parseFloat(a.pl) || 0) - (parseFloat(b.pl) || 0))
//                   .map((position, index) => (
//                     <tr key={position.id || index} className="h-7 hover:bg-zinc-700/30 border-b border-zinc-700/30">
//                       <td className="px-3 py-1 text-white border-r border-zinc-700/30 truncate">
//                         {(position.symbol || '').replace('NSE:', '')}
//                       </td>
//                       <td className="px-3 py-1 text-center text-zinc-300 border-r border-zinc-700/30 font-mono">
//                         {Math.abs(parseFloat(position.net_qty) || 0)}
//                       </td>
//                       <td className="px-3 py-1 text-right text-red-400 font-medium font-mono">
//                         {formatCurrency(parseFloat(position.pl) || 0)}
//                       </td>
//                     </tr>
//                   ))}
//               </tbody>
//             </table>
//           </div>
//         </div>

//         {/* Client Summary */}
//         <div className="bg-zinc-800 rounded border border-zinc-700">
//           <div className="px-4 py-3 border-b border-zinc-700">
//             <h2 className="text-sm font-semibold text-white">Client Summary</h2>
//           </div>

//           <div className=" overflow-x-auto max-h-96">
//             <table className="w-full text-xs min-w-max">
//               <thead className="bg-zinc-700/50">
//                 <tr className="h-7">
//                   <th className="px-3 py-1 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[120px]">Client</th>
//                   <th className="px-3 py-1 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[60px]">Positions</th>
//                   <th className="px-3 py-1 text-right text-zinc-300 font-medium min-w-[100px]">P&L</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {Object.entries(
//                   positions.reduce((acc, p) => {
//                     const client = p.client_id || 'N/A';
//                     if (!acc[client]) {
//                       acc[client] = { count: 0, pnl: 0 };
//                     }
//                     acc[client].count++;
//                     acc[client].pnl += (parseFloat(p.pl) || 0);
//                     return acc;
//                   }, {})
//                 ).map(([client, data], index) => (
//                   <tr key={index} className="h-7 hover:bg-zinc-700/30 border-b border-zinc-700/30">
//                     <td className="px-3 py-1 text-white border-r border-zinc-700/30 truncate">
//                       {client}
//                     </td>
//                     <td className="px-3 py-1 text-center text-zinc-300 border-r border-zinc-700/30 font-mono">
//                       {data.count}
//                     </td>
//                     <td className={`px-3 py-1 text-right font-medium font-mono ${
//                       data.pnl >= 0 ? 'text-green-400' : 'text-red-400'
//                     }`}>
//                       {formatCurrency(data.pnl)}
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         </div>

//          {/* Negative Positions  */}

//           <div className="lg:col-span-1 bg-zinc-800 rounded border border-zinc-700">
//           <div className="px-4 py-3 border-b border-zinc-700">
//             <h2 className="text-sm font-semibold text-white">Negaive Positions</h2>
//           </div>

//           <div className="overflow-x-auto">
//             <table className="w-full text-xs min-w-max">
//               <thead className="bg-zinc-700/50">
//                 <tr className="h-8">
//                   <th className="px-3 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[32px]">#</th>
//                   <th className="px-3 py-2 text-left text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[120px]">Symbol</th>
//                   <th className="px-3 py-2 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[60px]">ClientId</th>
//                   <th className="px-3 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">Qty</th>
//                   <th className="px-3 py-2 text-center text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">Strategy</th>
//                   <th className="px-3 py-2 text-right text-zinc-300 font-medium border-r border-zinc-600/30 min-w-[80px]">Action</th>
//                 </tr>
//               </thead>
//               <tbody>
//               {metrics.negativePositions.length === 0 ? (
//                 <tr>
//                   <td colSpan="6" className="px-3 py-8 text-center text-zinc-400 text-xl">
//                     No Negative Positions Open
//                   </td>
//                 </tr>
//               ) : (
//                 metrics.negativePositions.map((position, index) => {
//                   return (
//                     <tr key={position.id || index} className="h-8 hover:bg-zinc-700/30 border-b border-zinc-700/30">
//                       <td className="px-3 py-1 text-zinc-400 border-r border-zinc-700/30">{index + 1}</td>
//                       <td className="px-3 py-1 border-r border-zinc-700/30">
//                         <div className="text-white font-medium truncate">
//                           {position.symbol}
//                         </div>
//                       </td>
//                       <td className="px-3 py-1 text-right text-red-500 border-r border-zinc-700/30">
//                         {Math.abs(parseInt(position.net_qty))}
//                       </td>
//                       <td className="px-3 py-1 text-right text-white border-r border-zinc-700/30">
//                         {position.client_id}
//                       </td>
//                       <td className="px-3 py-1 text-center text-zinc-300 border-r border-zinc-700/30 truncate">
//                         {position.strategy}
//                       </td>
//                       <td className={`px-3 py-1 text-right font-medium font-mono `}>
//                         <Button
//                           onClick={() => handleExitPosition(position)}
//                           size="sm"
//                           variant="destructive"
//                           className="bg-red-900 hover:bg-red-800 text-red-200 text-xs px-3 py-1"
//                         >
//                           Close Position
//                         </Button>
//                       </td>
//                     </tr>
//                   );
//                 })
//               )}
//             </tbody>
//             </table>
//           </div>
//         </div>

//       </div>
//     </div>
//   );
// }

// export default CompactDashboard;
